import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './styles.css';

const Bitrix24Form = ({
  src,
  data,
  className,
  style,
  showAsPopup,
}) => {
  const containerRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!containerRef.current || !src || !data) return undefined;
    if (showAsPopup && !isPopupOpen) return undefined;

    // Create script element
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-b24-form', data);
    script.setAttribute('data-skip-moving', 'true');

    containerRef.current.appendChild(script);

    return () => {
      if (containerRef.current && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [src, data, isPopupOpen, showAsPopup]);

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  // If showAsPopup is true, render the sticky button
  if (showAsPopup) {
    return (
      <>
        {/* Sticky Button */}
        <button
          type="button"
          onClick={handleOpenPopup}
          className="bitrix24-sticky-button"
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-headset"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M4 14v-3a8 8 0 1 1 16 0v3" />
            <path d="M18 19c0 1.657 -2.686 3 -6 3" />
            <path d="M4 14a2 2 0 0 1 2 -2h1a2 2 0 0 1 2 2v3a2 2 0 0 1 -2 2h-1a2 2 0 0 1 -2 -2v-3z" />
            <path d="M15 14a2 2 0 0 1 2 -2h1a2 2 0 0 1 2 2v3a2 2 0 0 1 -2 2h-1a2 2 0 0 1 -2 -2v-3z" />
          </svg>
          {isButtonHovered && (
            <span className="bitrix24-button-text">
              { t('component.label.need_help', 'Need Help ?') }
            </span>
          )}
        </button>

        {/* Popup Overlay */}
        {isPopupOpen && (
          <div
            className="bitrix24-popup-overlay"
            onClick={handleClosePopup}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleClosePopup();
              }
            }}
            role="button"
            tabIndex={0}
          >
            {/* Popup Content */}
            <div
              className={`bitrix24-popup-content ${isPopupOpen ? 'open' : ''}`}
              role="presentation"
              tabIndex={-1}
              aria-label={t('component.label.contact_form_dialog', 'Contact form dialog')}
              onClick={(e) => { e.stopPropagation(); }}
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={handleClosePopup}
                className="bitrix24-close-button"
              >
                ×
              </button>

              {/* Form Container - Original size, uses 100% of popup size */}
              <div
                ref={containerRef}
                className="bitrix24-form-container"
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
    />
  );
};

Bitrix24Form.propTypes = {
  src: PropTypes.string.isRequired,
  data: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ])),
  showAsPopup: PropTypes.bool,
};

Bitrix24Form.defaultProps = {
  className: undefined,
  style: undefined,
  showAsPopup: false,
};

export default Bitrix24Form;
